'use client';
import React, { useEffect, useMemo, useRef } from 'react';
import { AccordionWithCustomContent } from '@workspace/ui/common/AccordionWithCustomContent';
import { PAGE_SIZE, dedupeById } from '@workspace/lib/document-hub-utils';
import { DocumentHubSectionContent } from './DocumentHubSectionContent';
import { Section, HubItem } from '@workspace/interfaces';
import { useAccordionState, useDocumentHubData, useTagDiscovery } from './hooks';

interface DocumentHubProps {
  sections: Section[];
  className?: string;
  filter?: (item: HubItem, section: Section) => boolean;
  pageSlug?: string;
  filterActive?: boolean;
  onTagsDiscovered?: (tags: { id: string; label: string }[]) => void;
  downloadCTA?: string;
  viewCTA?: string;
}

export function DocumentHub({
  sections,
  className,
  filter,
  pageSlug,
  filterActive = false,
  onTagsDiscovered,
  downloadCTA,
  viewCTA,
}: DocumentHubProps) {
  const { mergeDiscoveredFromFiles, resetDiscoveredTags } = useTagDiscovery(onTagsDiscovered);
  const {
    itemsBySection,
    loadingBySection,
    maxVisibleBySection,
    noMoreBySection,
    ensureLoaded,
    loadMore,
    showAll,
    totalFor,
    resetData,
  } = useDocumentHubData(sections, pageSlug, mergeDiscoveredFromFiles);

  const { openIds, setOpenIds } = useAccordionState(sections, ensureLoaded);

  const sectionsKey = useMemo(() => sections.map((s) => s.id).join('|'), [sections]);
  const prevKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevKeyRef.current === sectionsKey) return;
    prevKeyRef.current = sectionsKey;
    resetData();
    resetDiscoveredTags();
  }, [sectionsKey, resetData, resetDiscoveredTags]);

  const filteredMap = useMemo(() => {
    const map: Record<string, HubItem[]> = {};
    sections.forEach((section) => {
      const all = dedupeById(itemsBySection[section.id] ?? []);
      map[section.id] = filter ? all.filter((it) => filter(it, section)) : all;
    });
    return map;
  }, [sections, itemsBySection, filter]);

  const accordionSections = useMemo(() => {
    return sections
      .filter((s) => {
        if (!filterActive) return true;
        const list = filteredMap[s.id] ?? [];
        return list.length > 0;
      })
      .map((section) => {
        const filtered = filteredMap[section.id] ?? [];
        const visibleMax = maxVisibleBySection[section.id] ?? PAGE_SIZE;
        const itemsToShow = filtered.slice(0, visibleMax);

        const loadedCount = (itemsBySection[section.id] ?? []).length;
        const total = totalFor(section);
        const exhausted = !!noMoreBySection[section.id];
        const isAllFetched = loadedCount >= total || exhausted;
        const isAllVisible = visibleMax >= total || exhausted;
        const showControls = !(isAllFetched && isAllVisible);

        return {
          id: section.id,
          title: <>{section.title}</>,
          content: (
            <DocumentHubSectionContent
              section={section}
              itemsToShow={itemsToShow}
              hasAnyData={(itemsBySection[section.id] ?? []).length > 0}
              isLoading={!!loadingBySection[section.id]}
              showControls={showControls}
              onLoadMore={loadMore}
              onShowAll={showAll}
              downloadCTA={downloadCTA}
              viewCTA={viewCTA}
            />
          ),
        };
      });
  }, [
    sections,
    filteredMap,
    maxVisibleBySection,
    loadingBySection,
    filterActive,
    itemsBySection,
    totalFor,
    loadMore,
    showAll,
    noMoreBySection,
    downloadCTA,
    viewCTA,
  ]);

  return (
    <AccordionWithCustomContent
      sections={accordionSections}
      allowMultiple
      className={className}
      openIds={openIds}
      onChange={setOpenIds}
    />
  );
}
